import React from "react";

import { IconButton } from "./Button";
import { Icon, type IconName } from "./icons";
import { Floating, listKey, usePopover } from "./popover";

export type MenuItem = {
  label: string;
  onSelect: () => void;
  icon?: IconName;
  danger?: boolean;
  disabled?: boolean;
  checked?: boolean;
};

/** The row "⋯" menu (or any trigger): arrow keys, Enter, Escape, closes on
 * an outside press without a scrim catching it. */
export function Menu({
  items,
  label,
  trigger,
  align = "end",
}: {
  items: MenuItem[];
  label: string;
  /** Custom trigger; defaults to a "more" icon button. */
  trigger?: (p: {
    ref: React.Ref<HTMLButtonElement>;
    open: boolean;
    onClick: () => void;
    "aria-haspopup": "menu";
    "aria-expanded": boolean;
  }) => React.ReactNode;
  align?: "start" | "end";
}) {
  const pop = usePopover<HTMLButtonElement>(align);
  const list = React.useRef<HTMLDivElement | null>(null);
  const typed = React.useRef({ text: "", at: 0 });
  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    if (!pop.open) return;
    setActive(0);
    list.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
  }, [pop.open]);

  function move(i: number) {
    setActive(i);
    list.current?.querySelectorAll<HTMLElement>("[role=menuitem]")[i]?.focus();
  }

  const triggerProps = {
    ref: pop.trigger,
    open: pop.open,
    onClick: pop.toggle,
    "aria-haspopup": "menu" as const,
    "aria-expanded": pop.open,
  };

  return (
    <div ref={pop.root} className="ds-menu">
      {trigger ? (
        trigger(triggerProps)
      ) : (
        <IconButton
          ref={pop.trigger}
          icon="more"
          label={label}
          aria-haspopup="menu"
          aria-expanded={pop.open}
          onClick={(e) => {
            e.stopPropagation();
            pop.toggle();
          }}
        />
      )}
      {pop.open && (
        <Floating>
          <div
            ref={(el) => {
              list.current = el;
              pop.panel.current = el;
            }}
            style={pop.style}
            role="menu"
            aria-label={label}
            className="ds-pop menu"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Tab") {
                e.preventDefault();
                pop.close();
                return;
              }
              const next = listKey(
                e.key,
                active,
                items.map((i) => i.label),
                typed,
              );
              if (next != null) {
                e.preventDefault();
                move(next);
              }
            }}
          >
            {items.map((it, i) => (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                tabIndex={i === active ? 0 : -1}
                disabled={it.disabled}
                className={`ds-pop-item${it.danger ? " danger" : ""}`}
                onClick={() => {
                  pop.close();
                  it.onSelect();
                }}
              >
                {it.icon && <Icon name={it.icon} size={14} />}
                <span className="ds-pop-text">{it.label}</span>
                {it.checked && <Icon name="check" size={14} />}
              </button>
            ))}
          </div>
        </Floating>
      )}
    </div>
  );
}
